import { Grid, Tooltip } from "@material-ui/core";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import CustomInput from "components/CustomInput";
import DropZoneInDialog from "components/DropZoneInDialog";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import LoadingSpinner from "components/LoadingSpinner";
import NotificationProvider from "components/NotificationProvider";
import FadeInImage from "features/vehicles/components/FadeInImage";
import { useGetDocumentTypesQuery } from "features/vehicles/vehicleSlice";
import { IconAddCircle, IconPhoto } from "icons";
import { orderBy } from "lodash";
import { useState } from "react";
import FormatService from "services/formatService";
const { AttachmentsStyle } = require("./AttachmentsStyle");
const { useGetEntityAttachmentsQuery, useDeleteAttachmentMutation, useAddAttachmentMutation, useUpdateDocumentTypeMutation } = require("./attachmentSlice");

const AttachmentsView = ({
    attachmentsSent,
    entityId,
    entityType,
    showTitle = false,
    allowAdd = true,
    addBtnStyle = 'outlined',
    useIconButton,
    addPermissionProfile = permissionProfiles.ACTIVITY.EDIT_ACTIVITY_ATTACHMENTS,
    permissionDeleteImage,
    editPermissionProfile,
    label = 'Attachment',
    noteType,
}) => {
    const classes = AttachmentsStyle();

    let { data: attachmentsFromApi, error: attachmentsError, isFetching: isLoadingAttachments } = useGetEntityAttachmentsQuery({ entityId: entityId, entityType: entityType }, { skip: !entityId || !!attachmentsSent });
    let { data: documentTypes, error: documentTypeError, isLoading: isLoadingDocumentTypes } = useGetDocumentTypesQuery();

    const [deleteAttachment] = useDeleteAttachmentMutation();
    const [addAttachment, { isLoading: uploadingAttachment }] = useAddAttachmentMutation();
    const [updateDocumentType] = useUpdateDocumentTypeMutation();

    documentTypes = documentTypes || [];

    const [docTypeToEdit, setDocTypeToEdit] = useState();

    let attachments = attachmentsSent ?? attachmentsFromApi ?? [];

    attachments = attachments.map(i => ({
        ...i,
        createdDateFormatted: FormatService.formatDate(i.createdDate)
    }))
    attachments = orderBy(attachments, 'createdDate', ['desc']);

    let groupedAttachmentsByDate = {};
    attachments.forEach(img => {
        if (!groupedAttachmentsByDate[img.createdDateFormatted]) groupedAttachmentsByDate[img.createdDateFormatted] = [];
        groupedAttachmentsByDate[img.createdDateFormatted].push(img);
    });

    let attachmentsDates = Object.keys(groupedAttachmentsByDate);

    const onDelete = async (img) => {
        const attachmentToDelete = { ...img }
        const res = await deleteAttachment(attachmentToDelete);
        if (res.error) {
            NotificationProvider.error("Failed to delete the attachment");
        } else {
            NotificationProvider.success("Successfully deleted the attachment");
        }
    }

    const uploadAttachment = async (file) => {
        let fileBase64 = "";
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
            fileBase64 = reader.result.toString().replace(/^data:(.*,)?/, '');
            const result = await addAttachment({
                "fileName": file.name,
                "fileType": file.type,
                "filedata": fileBase64,
                "entityId": entityId,
                "entityType": entityType,
                "documentType": docTypeToEdit
            })
            if (result.error) {
                NotificationProvider.error(`Failed to upload the ${label}`);
            } else {
                NotificationProvider.success(`Successfully uploaded the  ${label}`);
                setDocTypeToEdit();
            }
        };
    };

    const onEdit = async (img) => {
        const result = await updateDocumentType({ ...img, "entityId": entityId, "entityType": entityType });
        if (result.error) {
            NotificationProvider.error(`Failed to update the document type`);
        } else {
            NotificationProvider.success(`Successfully updated the document type`);
            setDocTypeToEdit();
        }
    }

    return (
        <Grid container>
            <Grid container direction='row' justifyContent={showTitle ? "space-between" : "flex-end"}>
                {showTitle && <Grid item className={classes.icon} spacing={2}>
                    <IconPhoto />
                    <div className={classes.title}>{`${label}(${attachments?.length ?? 0})`}</div>
                </Grid>}
                {allowAdd && <Authorize profile={addPermissionProfile}>
                    <Grid item>
                        <DropZoneInDialog
                            labelDialog={`Add ${label}`}
                            buttonLabel={useIconButton ? <Tooltip title={`Add ${label}`}><IconAddCircle /></Tooltip> : `Add ${label}`}
                            btnStyle={addBtnStyle}
                            onSave={uploadAttachment}
                            formContent={
                                <div className={classes.documentTypes}>
                                    <CustomInput
                                        id="documentType"
                                        label='Document Type'
                                        elementType='dropdown'
                                        value={docTypeToEdit}
                                        values={documentTypes}
                                        onChange={(val) => setDocTypeToEdit(+val)}
                                        showEmpty
                                    />
                                </div>
                            }
                            accept={{
                                'image/jpeg': ['.jpg', '.jpeg'],
                                'application/pdf': ['.pdf'],
                                'image/png': ['.png']
                            }}
                            actionButtonLabel="Upload"
                        />
                        <LoadingSpinner loading={uploadingAttachment} />
                    </Grid>
                </Authorize>}
            </Grid>

            {attachmentsDates?.map((imageDate) =>
                <Grid container direction="column">
                    <Grid item>
                        <div>Date: {!!imageDate ? imageDate : 'Unknown'}</div>
                    </Grid>
                    <Grid container >
                        {groupedAttachmentsByDate[imageDate]?.map((img, i) =>
                            <Grid item key={i} className={classes.imageContainer}>
                                <FadeInImage
                                    img={img}
                                    imageGroupFieldName="createdDateFormatted"
                                    alt={`${label} ${i}`}
                                    onDelete={onDelete}
                                    existingImages={attachments}
                                    showCarousel={true}
                                    imagesForCarousel={groupedAttachmentsByDate}
                                    noteType={noteType}
                                    permissionDeleteImage={permissionDeleteImage}
                                    documentTypes={documentTypes}
                                    editPermissionProfile={editPermissionProfile}
                                    onEdit={onEdit}
                                />
                                {!!img?.imageTag && <div className={classes.imageTag}>{img?.imageTag}</div>}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            )}
            {(!isLoadingAttachments && !(!!attachments?.length)) && <EmptyPlaceholder text={`No ${label}s Found`} />}
        </Grid>
    )
}

export default AttachmentsView;